import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useDebounce } from "use-debounce";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import { ContractorService, ServiceTypesEnum } from "@propertelligent/client-api";
import ContractorTopHeader from "../../newComponents/listings/top-listing-headers/contractor-listing-top-header";
import ContractorCard from "../../newComponents/listings/listing-cards/contractor";
import AddContractorForm from "../../newComponents/contractor/AddContratorForm";
import EmptyState from "../../newComponents/emptystate";
import { GetImages } from "../../utils/GetImages";
import { usePermissions } from "../../hooks/usePermissions";
import { UserLoginData } from "../../components/common/charts/Enums";
import apiHelper from "../../utils/apiHelper";
import { defaultPageSize } from "../../constants/constants";
import {
  ALL_FILTER_KEY,
  resolveAllFilterCount,
  resolveOrphanedAggregateFilter,
  shouldResolveOrphanedAggregateFilter,
  useAggregateFilterCounts,
} from "../../hooks/useAggregateFilterCounts";
import { getApiContractorOrganizationlistCounts } from "../../services/listFilterCounts.service";
import { escapeDynamicLinqStringLiteral } from "../../utils/dynamicLinq";

// ─── Constants ────────────────────────────────────────────────────────────────

// Order here is the order the filter pills render in (see CONTRACTOR_FILTER_OPTIONS below,
// derived from these same keys) — keep it in the intended pill order, not alphabetical.
const SERVICE_TYPE_MAP: Record<string, number> = {
  Handyman: ServiceTypesEnum.Handyman,
  Plumber: ServiceTypesEnum.Plumbing,
  Electrician: ServiceTypesEnum.Electrical,
  Interior: ServiceTypesEnum.Interior,
  Exterior: ServiceTypesEnum.Exterior,
  Appliance: ServiceTypesEnum.Appliance,
  "General Contractor": ServiceTypesEnum.GeneralContractor,
  Other: ServiceTypesEnum.Other,
};

const CONTRACTOR_FILTER_OPTIONS = Object.keys(SERVICE_TYPE_MAP).map((title) => ({ title }));

// ─── Component ────────────────────────────────────────────────────────────────

const ContractorList = () => {
  const { canWrite } = usePermissions();

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState(ALL_FILTER_KEY);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [isModalOpen, setModalOpen] = useState(false);

  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) ?? "0");
  const tenantId = parseInt(sessionStorage.getItem(UserLoginData.tenant_id) ?? "0");
  const scopeId = orgId || tenantId;

  const searchOnlyFilter = useMemo(
    () =>
      debouncedSearchTerm
        ? `(User.FirstName + " " + User.LastName).ToLower().Contains("${escapeDynamicLinqStringLiteral(debouncedSearchTerm.toLowerCase())}")`
        : null,
    [debouncedSearchTerm],
  );

  const rowsFilter = useMemo(() => {
    const filters: string[] = [];

    if (searchOnlyFilter) {
      filters.push(searchOnlyFilter);
    }

    const enumId = SERVICE_TYPE_MAP[activeFilter];
    if (activeFilter !== ALL_FILTER_KEY && enumId) {
      filters.push(`ServiceTypesIds.Any(sid => sid == ${enumId})`);
    }

    return filters.length > 0 ? filters.join(" and ") : null;
  }, [searchOnlyFilter, activeFilter]);

  const { rows, loading, hasMore, fetchData, currentPage, aggregateValues } =
    infiniteScrollHook({
      httpFetchDataFn: ContractorService.getApiContractorOrganizationlist,
      FullyQualifiedFilters: rowsFilter,
      pageSize: 10,
      FullyQualifiedSorts: `User.FirstName ${sortDirection}`,
    });

  const fetchCounts = useCallback(async (filter: string | null) => {
    const res = await apiHelper(
      getApiContractorOrganizationlistCounts,
      { showNotification: false },
      scopeId,
      defaultPageSize,
      1,
      undefined,
      undefined,
      undefined,
      filter,
      null,
    );
    return res?.data?.aggregateValues ?? undefined;
  }, [scopeId]);

  const {
    aggregateValues: aggregateCounts,
    baselineAggregateValues,
    loading: countsLoading,
    refreshCounts,
    getVisibleOptions,
  } = useAggregateFilterCounts({
    fetchCounts,
    searchOnlyFilter,
    scopeKey: `contractor:${scopeId}`,
  });

  const allFilterCount = useMemo(
    () => resolveAllFilterCount(aggregateCounts),
    [aggregateCounts],
  );

  const baselineTotalCount = useMemo(() => {
    const source = baselineAggregateValues ?? aggregateCounts ?? aggregateValues;
    return resolveAllFilterCount(source);
  }, [baselineAggregateValues, aggregateCounts, aggregateValues]);

  const visibleFilterOptions = useMemo(
    () =>
      getVisibleOptions([
        { title: ALL_FILTER_KEY, count: allFilterCount },
        ...CONTRACTOR_FILTER_OPTIONS,
      ]),
    [allFilterCount, getVisibleOptions],
  );

  const handleFullRefresh = useCallback(() => {
    refreshCounts();
    fetchData(1);
  }, [fetchData, refreshCounts]);

  // When zero-count pills are hidden (e.g. the last contractor in a pill was just deleted),
  // the currently selected pill can become an orphan - fall back to All so rows stay
  // filtered to that empty bucket while hasAnyContractors is true - empty-state lie.
  // Wait for counts so a loading-time All-only pill set cannot reset deep-linked filters.
  useEffect(() => {
    if (!shouldResolveOrphanedAggregateFilter(countsLoading, aggregateCounts)) {
      return;
    }
    const resolved = resolveOrphanedAggregateFilter(activeFilter, visibleFilterOptions);
    if (resolved !== activeFilter) {
      setActiveFilter(resolved);
    }
  }, [countsLoading, aggregateCounts, visibleFilterOptions, activeFilter]);

  const handleFilterChange = useCallback((filter: string) => {
    if (filter !== activeFilter) setActiveFilter(filter);
  }, [activeFilter]);

  const handleSort = useCallback((dir: "asc" | "desc") => setSortDirection(dir), []);
  const handleOpenModal = useCallback(() => setModalOpen(true), []);
  const handleModalClose = useCallback(() => setModalOpen(false), []);

  const dataHookState = useMemo(
    () => ({ rows, loading, hasMore, fetchData, currentPage, handleFullRefresh }),
    [rows, loading, hasMore, fetchData, currentPage, handleFullRefresh]
  );

  // ─── Derived booleans ───────────────────────────────────────────────────────
  const isEmptyState = rows.length === 0 && !loading;
  const hasAnyContractors = baselineTotalCount > 0;
  const showInitialEmptyState = !hasAnyContractors && isEmptyState;
  const hasSearchResults =
    !showInitialEmptyState && isEmptyState && (searchTerm !== "" || activeFilter !== ALL_FILTER_KEY);

  return (
    <div className="content-wrapper container-fluid">
      {showInitialEmptyState ? (
        <div className="my-4 h-100">
          <EmptyState
            icon={GetImages.emptyStateContractor}
            heading="Add Your First Contractor"
            buttonLabel="Add Contractor"
            onButtonClick={handleOpenModal}
            isButton={canWrite("Contractor")}
          />
        </div>
      ) : (
        <>
          {hasAnyContractors && (
            <ContractorTopHeader
              PageHeading="Contractors"
              ButtonName="Add Contractor"
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              handleSort={handleSort}
              currentSortDirection={sortDirection}
              onClick={handleOpenModal}
              showAddButton={canWrite("Contractor")}
              MobileButtonName="Add"
              activeFilter={activeFilter}
              handleFilterChange={handleFilterChange}
              filterOptions={visibleFilterOptions}
            />
          )}

          {hasSearchResults ? (
            <div className="my-4 h-100">
              <EmptyState
                icon={GetImages.emptyStateContractor}
                heading="No Contractor Found"
                isButton={false}
              />
            </div>
          ) : (
            <ContractorCard
              id="contractors"
              isFooter={true}
              dataHookState={dataHookState}
            />
          )}
        </>
      )}

      {isModalOpen && (
        <AddContractorForm
          isModalOpen={isModalOpen}
          setModalOpen={handleModalClose}
          fetchData={handleFullRefresh}
        />
      )}
    </div>
  );
};

export default React.memo(ContractorList);